import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight, ChevronRight, Compass, Layers, Wrench, RefreshCcw,
  Youtube, PlayCircle, CheckCircle2, Quote,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { SERVICES } from '../data/services';
import { FEATURED_VIDEOS, CYBER_GUY_CHANNEL_URL } from '../data/videos';
import { FIELD_NOTES } from '../data/fieldNotes';
import YouTubeVideoCard from '../components/YouTubeVideoCard';
import SEO, { SITE, orgRef, websiteRef } from '../components/SEO';

/* -----------------------------------------------------------
   Focused homepage — introduction, not encyclopedia.
   Full detail lives on About, Services, Insights, Cyber Guy.
----------------------------------------------------------- */

const whyPoints = [
  { title: 'Practical, Not Theoretical', body: 'Security improvements you can understand, apply and operate — not a policy document that lives in a folder.' },
  { title: 'Tailored to Your Business',   body: 'Recommendations reflect your environment, your risks and how your business actually works.' },
  { title: 'Business-Focused Security',   body: 'The goal is reduced risk and a business that can operate, grow and recover — not more tools for their own sake.' },
];

const approachSteps = [
  { num: '01', icon: Compass,    title: 'Understand',  body: 'Identify assets, business priorities, threats and the security gaps that actually matter.' },
  { num: '02', icon: Layers,     title: 'Prioritize',  body: 'Sequence what to address first based on business impact, likelihood and effort.' },
  { num: '03', icon: Wrench,     title: 'Strengthen',  body: 'Implement practical controls, close the gaps and put ownership behind each one.' },
  { num: '04', icon: RefreshCcw, title: 'Maintain',    body: 'Review, monitor, educate and keep the program moving as the business changes.' },
];

// Homepage displays ONE featured incident. Full library lives in /insights.
const featuredIncident = {
  name: 'MGM Resorts',
  category: 'Real Cyber Incident',
  lesson: 'Identity is the modern perimeter — help desks and people are part of it.',
  short: 'Attackers used social engineering to gain identity-level access, then moved laterally across critical systems.',
};

// 3 representative Field Notes for homepage teaser
const homeNotes = [FIELD_NOTES[0], FIELD_NOTES[2], FIELD_NOTES[5]];

// 2 featured Cyber Guy videos
const homeVideos = FEATURED_VIDEOS.slice(0, 2);

const EyebrowDark = ({ children, testId }) => (
  <div className="inline-flex items-center gap-2.5 mb-4" data-testid={testId}>
    <span className="w-6 h-px bg-cyan-300/70" />
    <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-300/90 font-semibold">{children}</span>
  </div>
);

const EyebrowLight = ({ children, testId }) => (
  <div className="inline-flex items-center gap-2.5 mb-4" data-testid={testId}>
    <span className="w-6 h-px bg-blue-600/70" />
    <span className="text-[11px] uppercase tracking-[0.22em] text-blue-700 font-semibold">{children}</span>
  </div>
);

const HomePage = () => {
  return (
    <div className="min-h-screen bg-white pt-16 sm:pt-20">
      <SEO
        path="/"
        title="Kennyvicops Cyber Solutions | Practical Cybersecurity"
        description="Practical cybersecurity services for small and growing businesses. Assess risk, strengthen security, protect data, and build resilience."
        image="/kenny-vic.webp"
        jsonLd={[{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          '@id': `${SITE}/#webpage`,
          url: `${SITE}/`,
          name: 'Practical Cybersecurity for Small & Growing Businesses',
          isPartOf: websiteRef,
          about: orgRef,
          primaryImageOfPage: `${SITE}/kenny-vic.webp`,
        }]}
      />

      {/* ============================================================
         1. HERO — Dark navy, two-column, human founder presence
      ============================================================ */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_15%,rgba(22,135,255,0.18),transparent_55%),radial-gradient(circle_at_82%_85%,rgba(39,199,232,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="absolute inset-0 bg-kv-noise opacity-[0.08]" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/50 to-transparent" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 pt-14 sm:pt-24 pb-16 sm:pb-24">
          <div className="grid gap-10 lg:gap-16 lg:grid-cols-[1.15fr_1fr] lg:items-center">
            <div className="order-1">
              <EyebrowDark testId="hero-eyebrow">Practical Cybersecurity for Small &amp; Growing Businesses</EyebrowDark>

              <h1 className="font-editorial text-[2.5rem] leading-[1.05] sm:text-6xl lg:text-[4.25rem] font-semibold tracking-tight text-white">
                Practical Cybersecurity for Small &amp; Growing Businesses.
              </h1>

              <p className="mt-6 text-lg sm:text-xl text-cyan-100/90 font-medium max-w-2xl">
                Understand Your Risks. Strengthen Your Security. Build Resilience.
              </p>

              <p className="mt-5 text-base sm:text-lg text-slate-300/95 leading-relaxed max-w-2xl">
                Cybersecurity shouldn&apos;t begin with a product or a pile of jargon. It should begin
                with understanding what matters to your business, where the risks are, and what needs
                attention first.
              </p>

              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <Link to="/contact" className="w-full sm:w-auto">
                  <Button
                    data-testid="hero-cta-consultation"
                    className="w-full sm:w-auto px-7 py-6 text-sm sm:text-base bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold tracking-tight shadow-lg shadow-cyan-500/10"
                  >
                    Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
                <Link to="/services" className="w-full sm:w-auto">
                  <Button
                    data-testid="hero-cta-services"
                    variant="outline"
                    className="w-full sm:w-auto px-7 py-6 text-sm sm:text-base bg-transparent border-cyan-300/50 text-cyan-100 hover:bg-white/5 hover:text-white rounded-full"
                  >
                    Explore Our Services
                  </Button>
                </Link>
              </div>

              <div className="mt-10 flex flex-wrap gap-x-4 gap-y-2 text-xs sm:text-[13px] text-slate-400">
                {SERVICES.map((s, i) => (
                  <React.Fragment key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="hover:text-cyan-300 transition-colors">
                      {s.name}
                    </Link>
                    {i < SERVICES.length - 1 && <span className="text-slate-700">/</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>

            <div className="order-2 relative">
              <div className="relative mx-auto max-w-sm lg:max-w-none">
                <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-br from-cyan-400/25 via-blue-500/10 to-transparent rounded-[2rem] blur-2xl" aria-hidden />
                <div className="absolute -top-3 -left-3 w-24 h-24 border border-cyan-300/40 rounded-tl-[1.5rem]" aria-hidden />
                <div className="absolute -bottom-3 -right-3 w-24 h-24 border border-cyan-300/40 rounded-br-[1.5rem]" aria-hidden />

                <figure className="relative rounded-[1.5rem] overflow-hidden ring-1 ring-white/10 bg-slate-900">
                  <img
                    src="/kenny-vic.webp"
                    alt="Kenny Vic — Founder & Principal Cybersecurity Consultant, Kennyvicops"
                    className="w-full h-auto object-cover aspect-[4/5]"
                    loading="eager"
                    fetchpriority="high"
                    data-testid="hero-founder-photo"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#06101F] via-[#06101F]/20 to-transparent" />

                  <figcaption className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
                    <div className="text-[10px] uppercase tracking-[0.24em] text-cyan-300/90 mb-1.5">Founder</div>
                    <div className="font-editorial text-2xl sm:text-3xl text-white leading-none">Kenny Vic</div>
                    <div className="mt-1 text-sm text-slate-300">Principal Cybersecurity Consultant</div>
                    <p className="mt-3 text-sm text-slate-300/90 italic max-w-xs">
                      &ldquo;Security should work in the real world &mdash; not just on paper.&rdquo;
                    </p>
                  </figcaption>
                </figure>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
         2. BUSINESS PROBLEM — White background, editorial
      ============================================================ */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-3xl">
            <EyebrowLight>The Business Problem</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl leading-[1.1] font-semibold tracking-tight text-[#06101F]">
              Cybersecurity should protect your business &mdash; not complicate it.
            </h2>
            <div className="mt-6 space-y-5 text-base sm:text-lg text-[#475467] leading-relaxed">
              <p>
                Many small and growing businesses know cybersecurity matters, but don&apos;t always
                know where to start, what deserves attention first, or whether existing controls
                are actually working.
              </p>
              <p className="text-[#101828] font-medium">
                Kennyvicops helps turn cybersecurity from a collection of tools into a practical
                business capability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
         3. SERVICES BRIDGE — short elegant statement, single CTA
         (Full six-service grid lives on /services)
      ============================================================ */}
      <section className="relative bg-[#F5F8FC] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-16 sm:py-20 text-center">
          <EyebrowLight testId="services-bridge-eyebrow">Our Services</EyebrowLight>
          <h2 className="font-editorial text-2xl sm:text-4xl font-semibold text-[#06101F] leading-[1.15]">
            From understanding your risks to strengthening the controls that matter, Kennyvicops helps businesses build practical security.
          </h2>
          <div className="mt-8">
            <Link to="/services">
              <Button
                data-testid="home-explore-services"
                className="px-7 py-6 bg-[#06101F] hover:bg-[#0B1B35] text-white rounded-full font-semibold"
              >
                Explore Our Services <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
         4. WHY KENNYVICOPS — Dark navy positioning statement
      ============================================================ */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-kv-grid opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_75%_30%,rgba(22,135,255,0.15),transparent_55%)]" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <EyebrowDark>Why Kennyvicops</EyebrowDark>
            <h2 className="font-editorial text-3xl sm:text-5xl leading-[1.08] font-semibold tracking-tight text-white">
              Cybersecurity that fits the way you actually run your business.
            </h2>
          </div>

          <div className="grid gap-px bg-white/10 rounded-2xl overflow-hidden md:grid-cols-3">
            {whyPoints.map((p, i) => (
              <div
                key={p.title}
                data-testid={`why-point-${i}`}
                className="bg-[#06101F] p-8 sm:p-10 flex flex-col"
              >
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-editorial text-2xl text-cyan-300 font-semibold">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="w-8 h-px bg-cyan-300/60" />
                </div>
                <h3 className="font-editorial text-2xl sm:text-[26px] leading-tight text-white font-semibold mb-4">
                  {p.title}
                </h3>
                <p className="text-[15px] text-slate-300/90 leading-relaxed">{p.body}</p>
              </div>
            ))}
          </div>

          {/* Micro-bridge to About */}
          <div className="mt-12 sm:mt-14 text-center">
            <Link
              to="/about"
              data-testid="home-meet-founder"
              className="inline-flex items-center gap-2 text-sm font-semibold text-cyan-300 hover:text-cyan-200 border-b border-cyan-300/30 hover:border-cyan-200/70 pb-0.5"
            >
              Learn more about the experience behind Kennyvicops <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
         5. PRACTICAL APPROACH — Off-white, numbered steps
      ============================================================ */}
      <section className="relative bg-[#FAFBFD] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <EyebrowLight>Our Practical Approach</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl leading-[1.08] font-semibold tracking-tight text-[#06101F]">
              A practical path from understanding risk to building resilience.
            </h2>
          </div>

          <div className="relative">
            <div className="hidden md:block absolute top-14 left-[10%] right-[10%] h-px bg-gradient-to-r from-transparent via-blue-500/40 to-transparent" aria-hidden />

            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4 relative">
              {approachSteps.map((step, idx) => (
                <div
                  key={step.title}
                  data-testid={`approach-${step.title.toLowerCase()}`}
                  className="relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 hover:border-blue-500/40 hover:shadow-md hover:shadow-slate-900/5 transition-all"
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-editorial text-3xl font-semibold text-blue-600">{step.num}</span>
                    <div className="w-10 h-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                      <step.icon className="w-5 h-5 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="font-editorial text-xl font-semibold text-[#06101F] mb-2">{step.title}</h3>
                  <p className="text-sm text-[#475467] leading-relaxed">{step.body}</p>

                  {idx < approachSteps.length - 1 && (
                    <ChevronRight className="hidden lg:block absolute -right-4 top-14 w-6 h-6 text-blue-500/50 bg-[#FAFBFD] rounded-full" aria-hidden />
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================================================
         6. REAL EXPERIENCE BEHIND THE ADVICE — 3 featured Field Notes
         (Full library reachable via Insights CTA)
      ============================================================ */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-3xl mb-12 sm:mb-16">
            <EyebrowLight>Real Experience Behind the Advice</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl leading-[1.08] font-semibold tracking-tight text-[#06101F]">
              What real security work has taught me.
            </h2>
            <p className="mt-5 text-base sm:text-lg text-[#475467] leading-relaxed">
              Practical lessons from years of hands-on cybersecurity work &mdash; the kind of insight that shapes how Kennyvicops approaches every engagement.
            </p>
          </div>

          <div className="grid gap-5 md:grid-cols-3">
            {homeNotes.map((n) => (
              <article
                key={n.id}
                data-testid={`field-note-${n.id}`}
                className="group relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-7 flex flex-col hover:border-blue-500/40 hover:shadow-lg hover:shadow-slate-900/5 transition-all"
              >
                <div className="flex items-center justify-between mb-6">
                  <div className="inline-flex items-center gap-2">
                    <Quote className="w-3.5 h-3.5 text-blue-600" />
                    <span className="text-[10px] uppercase tracking-[0.24em] text-blue-700 font-semibold">
                      {n.label}
                    </span>
                  </div>
                  <span className="font-editorial text-2xl text-slate-300 font-semibold">{n.number}</span>
                </div>

                <h3 className="font-editorial text-xl sm:text-[22px] leading-[1.25] text-[#06101F] font-semibold mb-4">
                  {n.title}
                </h3>

                <p className="text-[15px] text-[#475467] leading-relaxed flex-1">{n.body}</p>

                <div className="mt-6 pt-5 border-t border-slate-100">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1.5 font-semibold">Lesson</div>
                  <p className="text-[15px] text-[#101828] font-medium leading-snug">{n.lesson}</p>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 text-center">
            <Link to="/insights">
              <Button
                data-testid="home-explore-insights"
                variant="outline"
                className="px-7 py-6 bg-white border-slate-300 text-[#101828] hover:bg-slate-50 rounded-full font-medium"
              >
                Explore All Insights <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ============================================================
         HEALTH CHECK CTA — subtle 1-line bridge (light neutral)
      ============================================================ */}
      <section className="relative bg-[#F5F8FC] bg-kv-grid-light text-[#101828] border-y border-slate-200/70">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-10 sm:py-12">
          <Link
            to="/security-health-check"
            data-testid="home-healthcheck-inline"
            className="group flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6 text-center sm:text-left"
          >
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-blue-700 font-semibold mb-1.5">Free Tool · 60 seconds</div>
              <p className="font-editorial text-lg sm:text-xl text-[#06101F] font-semibold leading-snug">
                Not sure where to start? Take the free 60-second Security Health Check.
              </p>
            </div>
            <span className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#06101F] text-white text-sm font-semibold group-hover:bg-[#0B1B35] transition-colors flex-shrink-0">
              Start the Health Check <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
            </span>
          </Link>
        </div>
      </section>

      {/* ============================================================
         7. THE CYBER GUY — 2 featured videos + CTA to full library
      ============================================================ */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_15%_20%,rgba(39,199,232,0.12),transparent_55%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-60" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="grid gap-12 lg:grid-cols-[1fr_1.1fr] lg:items-center mb-12">
            <div>
              <EyebrowDark>The Cyber Guy</EyebrowDark>
              <h2 className="font-editorial text-3xl sm:text-5xl leading-[1.08] font-semibold tracking-tight text-white">
                Cybersecurity explained in plain English.
              </h2>
              <div className="mt-6 space-y-4 text-base sm:text-lg text-slate-300/95 leading-relaxed">
                <p>
                  <span className="text-white font-semibold">The Cyber Guy</span> is Kenny Vic&apos;s public
                  cybersecurity education brand &mdash; short videos, real-world examples, practical security lessons.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-xl">
                <div className="rounded-xl border border-cyan-300/25 bg-white/[0.03] p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-300 font-semibold mb-1">The Cyber Guy</div>
                  <div className="text-sm text-white font-medium">Public cybersecurity education</div>
                </div>
                <div className="rounded-xl border border-cyan-300/25 bg-white/[0.03] p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-cyan-300 font-semibold mb-1">Kennyvicops</div>
                  <div className="text-sm text-white font-medium">Professional cybersecurity services</div>
                </div>
              </div>
            </div>

            {/* 2 featured video tiles */}
            <div className="grid gap-4 sm:grid-cols-2">
              {homeVideos.map((v) => (
                <YouTubeVideoCard key={v.youtubeId} video={v} compact />
              ))}
            </div>
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Link to="/the-cyber-guy">
              <Button
                data-testid="home-view-all-videos"
                className="px-6 py-5 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold"
              >
                Watch All Cyber Guy Videos <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a
              href={CYBER_GUY_CHANNEL_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm text-cyan-300 hover:text-cyan-200"
              data-testid="home-yt-subscribe"
            >
              <PlayCircle className="w-4 h-4" />
              Visit &amp; subscribe on YouTube
            </a>
          </div>
        </div>
      </section>

      {/* ============================================================
         8. ONE REAL CYBER INCIDENT — single teaser, links to Insights
      ============================================================ */}
      <section className="relative bg-white bg-kv-grid-light text-[#101828]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-2xl mb-10">
            <EyebrowLight>Real Cyber Attacks. Real Business Lessons.</EyebrowLight>
            <h2 className="font-editorial text-3xl sm:text-5xl leading-[1.08] font-semibold tracking-tight text-[#06101F]">
              Public incidents can reveal what happens when security fails.
            </h2>
          </div>

          <article
            data-testid="home-featured-incident"
            className="group relative bg-white border border-slate-200 rounded-2xl p-6 sm:p-9 hover:border-blue-500/40 hover:shadow-lg hover:shadow-slate-900/5 transition-all"
          >
            <div className="flex items-center gap-2 mb-5">
              <span className="text-[10px] uppercase tracking-[0.24em] text-blue-700 font-semibold">
                {featuredIncident.category}
              </span>
            </div>
            <h3 className="font-editorial text-2xl sm:text-3xl font-semibold text-[#06101F] mb-4 leading-tight">
              {featuredIncident.name}
            </h3>
            <p className="text-[15px] sm:text-base text-[#475467] leading-relaxed mb-6">
              {featuredIncident.short}
            </p>
            <div className="pt-5 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-1.5 font-semibold">Business Lesson</div>
              <p className="text-[15px] sm:text-base text-[#101828] font-medium leading-snug">
                {featuredIncident.lesson}
              </p>
            </div>
            <Link
              to="/insights"
              data-testid="home-incident-cta"
              className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-blue-600 hover:text-blue-700"
            >
              Read the Insight <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </Link>
          </article>

          <div className="mt-10 text-center">
            <Link to="/insights">
              <Button
                variant="outline"
                data-testid="home-explore-insights-2"
                className="px-7 py-6 bg-white border-slate-300 text-[#101828] hover:bg-slate-50 rounded-full font-medium"
              >
                Explore All Insights <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <p className="mt-10 text-xs text-slate-500 max-w-3xl mx-auto text-center">
            Case studies are based on publicly reported incidents and are presented for cybersecurity
            education and business risk awareness. They do not imply that any specific Kennyvicops
            control would definitely have prevented the incident.
          </p>
        </div>
      </section>

      {/* ============================================================
         9. FINAL CTA — Dark navy, closing
      ============================================================ */}
      <section className="relative overflow-hidden bg-[#06101F] text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_120%,rgba(22,135,255,0.25),transparent_50%)]" />
        <div className="absolute inset-0 bg-kv-grid opacity-70" />
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyan-400/40 to-transparent" />

        <div className="relative max-w-5xl mx-auto px-4 sm:px-6 py-24 sm:py-32 text-center">
          <EyebrowDark>Start with a Security Conversation</EyebrowDark>
          <h2 className="font-editorial text-3xl sm:text-5xl lg:text-6xl leading-[1.05] font-semibold tracking-tight text-white">
            Don&apos;t guess where your biggest cybersecurity risks are.
          </h2>
          <p className="mt-6 text-base sm:text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Start with a practical conversation about your environment, your priorities and where
            security can be strengthened.
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/contact">
              <Button
                data-testid="final-cta"
                className="w-full sm:w-auto px-8 py-6 bg-white text-[#06101F] hover:bg-cyan-100 rounded-full font-semibold tracking-tight"
              >
                Request a Security Consultation <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
            <Link to="/services">
              <Button
                variant="outline"
                data-testid="final-cta-services"
                className="w-full sm:w-auto px-8 py-6 bg-transparent border-cyan-300/50 text-cyan-100 hover:bg-white/5 hover:text-white rounded-full"
              >
                Explore Our Services
              </Button>
            </Link>
          </div>

          <div className="mt-10 inline-flex items-center gap-2 text-xs uppercase tracking-[0.24em] text-cyan-300/70">
            <CheckCircle2 className="w-3.5 h-3.5" />
            Practical &middot; Human &middot; Business-Focused
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
